'use client';

import { AnalyticsFeatureViewed } from '@cc-shopping/platform-analytics/AnalyticsFeatureViewed';
import { useAnalyticsPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView/Context';
import { useAnalyticsSkuViewedContext } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed/Context';
import { useAnalytics } from '@cc-shopping/platform-analytics/hooks/useAnalytics';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { useIdentityContext } from '@cc-shopping/platform-analytics/IdentityContext';
import { SEGMENT_DEPLOYABLE_NAME } from '@cc-shopping/platform-analytics/lib/constants';
import { getCleanLocationHref } from '@cc-shopping/platform-analytics/lib/urlUtils';
import { auth0 } from '@cc-shopping/platform-auth0';
import { env } from '@cc-shopping/platform-env';
import { InfoTooltip } from '@cc-shopping/ui/composites/InfoTooltip';
import { Box, Link, Typography } from '@mui/material';
import { useRef } from 'react';
import { v4 } from 'uuid';
import type { ApprovalOddsAnalytics } from '.';
import {
  CTA_LABEL,
  ENTITY_NAME,
  RECOMMENDED_CREDIT_LEARN_MORE_PATH,
  RECOMMENDED_CREDIT_TOOLTIP_TEXT,
  SECTION_NAME,
} from './constants';

export interface ApprovalOddsButtonProps {
  analytics?: ApprovalOddsAnalytics;
  hideTooltip?: boolean;
  firePageActionOnClick?: boolean;
}

const NW_ORIGIN = env.NEXT_PUBLIC_NW_URL;

const DASHBOARD_PATH = '/home/dashboard/home';

const ApprovalOddsButtonInner = ({
  analytics,
  firePageActionOnClick,
}: {
  analytics: ApprovalOddsAnalytics;
  firePageActionOnClick?: boolean;
}) => {
  const {
    sectionPosition = 0,
    elementPosition = 0,
    sectionName = SECTION_NAME,
    parentActivityId,
  } = analytics;
  const { deviceId, analytics: segmentAnalytics, deployableVersion } = useAnalytics();
  const { pageName, pageViewId, contentId, contentSource } = useAnalyticsPageView();
  const { skuImpressionId } = useAnalyticsSkuViewedContext();
  const { isLoggedIn } = useIdentityContext();
  const pageActionUuidRef = useRef(v4());
  const featureViewedUuidRef = useRef(v4());

  const openApprovalOdds = usePageAction({
    actionName: ENTITY_NAME,
    actionSection: sectionName,
    actionType: 'open',
    activityId: pageActionUuidRef.current,
    parentActivityId: featureViewedUuidRef.current,
    additionalAttributes: {
      sku_impression_id: skuImpressionId,
    },
  });

  return (
    <AnalyticsFeatureViewed
      entityName={ENTITY_NAME}
      entitySection={sectionName}
      impressionPosition={elementPosition || sectionPosition}
      activityId={featureViewedUuidRef.current}
      parentActivityId={parentActivityId}
      additionalAttributes={{
        sku_impression_id: skuImpressionId,
        target_flow: 'credit-score',
        cta_mode: 'modal',
        cta_format: 'link',
        cta_label: CTA_LABEL,
      }}
    >
      <Typography
        component="button"
        sx={theme => ({
          color: theme.palette.blues.main,
          fontSize: '0.8rem',
          fontWeight: 'normal',
          textAlign: 'left',
          cursor: 'pointer',
          textTransform: 'none',
          border: 'none',
          background: 'none',
          paddingX: 0,
          '&:hover': {
            textDecoration: 'underline',
          },
        })}
        onClick={async () => {
          if (isLoggedIn) {
            window.location.assign(DASHBOARD_PATH);
            return;
          }

          const currentURL = new URL(getCleanLocationHref());
          const urlWithoutSearchParams = `${currentURL.origin}${currentURL.pathname}`;
          const anonymousUserId =
            (await segmentAnalytics?.segmentInstance?.user())?.anonymousId() || '';

          const redirectTo = encodeURI(
            `${NW_ORIGIN}home/register?flow=credit-score&redirect_uri=${urlWithoutSearchParams}`,
          );

          const options: Parameters<typeof auth0.signUp>[0] = {
            anonymousUserId,
            cancelUrl: urlWithoutSearchParams,
            deployableName: SEGMENT_DEPLOYABLE_NAME,
            deployableVersion,
            driverContentId: contentId?.toString() ?? '',
            driverContentSource: contentSource?.toString() ?? '',
            driverElement: 'link',
            driverLocation: 'product_card',
            driverPageName: pageName,
            driverPageUrl: urlWithoutSearchParams,
            driverPageViewId: pageViewId,
            driverSubLocation: 'see_your_approval_odds',
            mostRecentTraditionalDeviceId: deviceId,
            redirectTo,
          };

          if (firePageActionOnClick) {
            openApprovalOdds(null, () => {
              auth0.signUp(options);
            });
          } else {
            auth0.signUp(options);
          }
        }}
      >
        {CTA_LABEL}
      </Typography>
    </AnalyticsFeatureViewed>
  );
};

export const ApprovalOddsButton = ({
  analytics = {},
  hideTooltip = false,
  firePageActionOnClick,
}: ApprovalOddsButtonProps) => {
  return (
    <Box>
      <ApprovalOddsButtonInner
        analytics={analytics}
        firePageActionOnClick={firePageActionOnClick}
      />
      {!hideTooltip && (
        <InfoTooltip
          ariaLabel="More information about Recommended Credit Score"
          maxWidth={235}
          text={
            <Typography variant="textSmall" sx={theme => ({ color: theme.palette.common.black })}>
              {RECOMMENDED_CREDIT_TOOLTIP_TEXT}{' '}
              <Link
                href={`${NW_ORIGIN}${RECOMMENDED_CREDIT_LEARN_MORE_PATH}`}
                underline="hover"
                target="_blank"
                rel="noopener"
              >
                Read more.
              </Link>
            </Typography>
          }
        />
      )}
    </Box>
  );
};
